import { useEffect, useState, useCallback, useMemo } from "react";
import api from "@/lib/api";
import { useAuth, isLocationUser, homeLocationOf, isAdmin } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import { formatApiError, fmtNum, LOCATIONS, statusBadgeClass } from "@/lib/utils";
import { ArrowRightLeft, CheckCircle2, Truck, Plus, X, PackageCheck, Search, Trash2 } from "lucide-react";

export default function Transfer() {
  const { user } = useAuth();
  const home = homeLocationOf(user);
  const locked = isLocationUser(user);

  const [batches, setBatches] = useState([]);
  const [from, setFrom] = useState(home || "FACTORY");
  const [to, setTo] = useState(LOCATIONS.find((l) => l !== (home || "FACTORY")) || "MAGHVEERA");
  const [picks, setPicks] = useState({}); // { batch_id: { bags } }
  const [vehicle, setVehicle] = useState("");
  const [notes, setNotes] = useState("");
  const [history, setHistory] = useState([]);
  const [historySearch, setHistorySearch] = useState("");
  const [inTransit, setInTransit] = useState([]);
  const [err, setErr] = useState("");
  const [success, setSuccess] = useState(null);
  const [loading, setLoading] = useState(false);
  const [splitTarget, setSplitTarget] = useState(null); // in_transit batch being unloaded
  const [splitLots, setSplitLots] = useState([]); // [{batch_number, bags, unload_date, notes}]
  const [splitErr, setSplitErr] = useState("");
  const [splitLoading, setSplitLoading] = useState(false);
  const [shortageDrafts, setShortageDrafts] = useState({}); // { batch_id: text }
  const [savingShortage, setSavingShortage] = useState(null);
  const [deletingTransferId, setDeletingTransferId] = useState(null);
  const [historyErr, setHistoryErr] = useState("");
  const canDeleteTransfer = isAdmin(user);

  const load = useCallback(async () => {
    const [approved, allBatches, t] = await Promise.all([
      api.get("/batches", { params: { status: "approved" } }),
      api.get("/batches", { params: { status: "in_transit" } }),
      api.get("/transfers"),
    ]);
    setBatches(approved.data.filter((x) => x.bags_remaining > 0));
    setInTransit(allBatches.data);
    setHistory(t.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openSplitModal = useCallback((b) => {
    const today = new Date().toISOString().slice(0, 10);
    const sourceBn = b.transfer_from?.batch_number || b.batch_number;
    const shortage = b.shortage_bags || 0;
    const pending = (b.bags_remaining ?? b.bags_count) - shortage;
    setSplitTarget(b);
    setSplitErr("");
    // Default lot name is a hint based on the source batch; receiver edits freely.
    setSplitLots([{ _id: `lot-${Date.now()}-0`, batch_number: `${sourceBn}-1`, bags: Math.max(0, pending), unload_date: today, notes: "" }]);
  }, []);

  const closeSplitModal = useCallback(() => {
    setSplitTarget(null);
    setSplitLots([]);
    setSplitErr("");
  }, []);

  const saveShortage = async (b) => {
    setErr(""); setSavingShortage(b.id);
    try {
      const draft = shortageDrafts[b.id] || {};
      const notes = (draft.notes !== undefined ? draft.notes : (b.shortage_notes || "")).trim();
      const bags = parseInt(draft.bags !== undefined ? draft.bags : (b.shortage_bags || 0), 10) || 0;
      const type = draft.type !== undefined ? draft.type : (b.discrepancy_type || "short");
      await api.patch(`/batches/${b.id}/shortage`, {
        shortage_notes: notes,
        shortage_bags: bags,
        discrepancy_type: type,
      });
      await load();
      setShortageDrafts((prev) => { const n = { ...prev }; delete n[b.id]; return n; });
    } catch (e) { setErr(formatApiError(e)); }
    finally { setSavingShortage(null); }
  };

  const deleteTransfer = async (t, { force = false } = {}) => {
    setHistoryErr("");
    if (!force) {
      const summary = `Delete transfer ${t.from_location} → ${t.to_location} · ${fmtNum(t.total_bags)} bags?\n\nSource stock will be restored and destination lots removed. This action is undoable from the Recently Deleted page (Admin/CEO).`;
      if (!window.confirm(summary)) return;
    }
    setDeletingTransferId(t.id);
    try {
      await api.delete(`/transfers/${t.id}`, { params: force ? { force: true } : {} });
      await load();
    } catch (e) {
      const msg = formatApiError(e);
      // If destination is already consumed, offer to force-delete (audit-only).
      if (e?.response?.status === 400 && !force && /partly used|has been partly/i.test(msg)) {
        const yes = window.confirm(
          `${msg}\n\nDo you still want to REMOVE THIS TRANSFER RECORD from the history? Stock will NOT be adjusted — the consumed bags stay accounted for at the destination. Recommended only for cleanup / audit corrections.`
        );
        if (yes) {
          await deleteTransfer(t, { force: true });
          return;
        }
      }
      setHistoryErr(msg);
    }
    finally { setDeletingTransferId(null); }
  };

  const updateLot = (idx, field, value) => {
    setSplitLots((prev) => prev.map((l, i) => (i === idx ? { ...l, [field]: value } : l)));
  };

  const addLotRow = () => {
    const today = new Date().toISOString().slice(0, 10);
    const sourceBn = splitTarget?.transfer_from?.batch_number || splitTarget?.batch_number || "";
    const nextIdx = splitLots.length + 1;
    setSplitLots((prev) => [...prev, { _id: `lot-${Date.now()}-${prev.length}`, batch_number: sourceBn ? `${sourceBn}-${nextIdx}` : "", bags: 0, unload_date: today, notes: "" }]);
  };

  const removeLotRow = (idx) => {
    setSplitLots((prev) => prev.filter((_, i) => i !== idx));
  };

  const submitSplit = async () => {
    setSplitErr("");
    if (!splitTarget) return;
    const shortage = splitTarget.shortage_bags || 0;
    const pending = (splitTarget.bags_remaining ?? splitTarget.bags_count) - shortage;
    const totalAssigned = splitLots.reduce((s, l) => s + (parseInt(l.bags, 10) || 0), 0);
    if (totalAssigned <= 0) { setSplitErr("Assign at least one bag to a lot"); return; }
    if (totalAssigned > pending) {
      setSplitErr(`Lot bag total (${totalAssigned}) exceeds bags available to unload (${pending})${shortage > 0 ? ` — ${shortage} bags are recorded as shortage` : ""}`); return;
    }
    for (const l of splitLots) {
      if (!l.batch_number.trim()) { setSplitErr("Every lot needs a batch/lot number"); return; }
      if (!l.unload_date) { setSplitErr("Every lot needs an unload date"); return; }
      if ((parseInt(l.bags, 10) || 0) <= 0) { setSplitErr("Every lot needs bags > 0"); return; }
    }
    const seen = new Set();
    for (const l of splitLots) {
      const bn = l.batch_number.trim();
      if (seen.has(bn)) { setSplitErr(`Duplicate lot number: ${bn}`); return; }
      seen.add(bn);
    }
    setSplitLoading(true);
    try {
      await api.post(`/batches/${splitTarget.id}/unload-split`, {
        lots: splitLots.map((l) => ({
          batch_number: l.batch_number.trim(),
          bags: parseInt(l.bags, 10) || 0,
          unload_date: l.unload_date,
          notes: l.notes || "",
        })),
      });
      closeSplitModal();
      await load();
    } catch (e) {
      setSplitErr(formatApiError(e));
    } finally {
      setSplitLoading(false);
    }
  };

  // Incoming = in_transit batches at the user's location (location users) or all (admin/QC)
  const incoming = useMemo(() => {
    if (locked) return inTransit.filter((b) => (b.location || "FACTORY") === home);
    return inTransit;
  }, [inTransit, locked, home]);

  const sourceBatches = useMemo(
    () => batches.filter((b) => (b.location || "FACTORY") === from),
    [batches, from]
  );

  const toLocationOptions = useMemo(
    () => LOCATIONS.filter((l) => l !== from),
    [from]
  );

  const filteredHistory = useMemo(() => {
    const term = historySearch.trim().toLowerCase();
    if (!term) return history;
    return history.filter((t) => {
      const hay = [
        t.from_location, t.to_location, t.vehicle, t.notes, t.created_by_name,
        ...(t.items || []).map((i) => i.source_batch_number),
      ].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(term);
    });
  }, [history, historySearch]);

  const updateBags = (b, val) => {
    const num = parseInt(val, 10);
    const next = { ...picks };
    if (!val || isNaN(num) || num <= 0) {
      delete next[b.id];
    } else {
      const capped = Math.min(num, b.bags_remaining);
      next[b.id] = { bags: capped };
    }
    setPicks(next);
  };

  const selected = Object.entries(picks)
    .map(([bid, v]) => {
      const src = batches.find((b) => b.id === bid);
      return src ? { src, bags: v.bags } : null;
    })
    .filter(Boolean);

  const totalBags = selected.reduce((s, x) => s + x.bags, 0);

  const submit = async () => {
    setErr(""); setSuccess(null); setLoading(true);
    try {
      if (from === to) throw new Error("Choose a different destination");
      // Destination batch numbers are assigned at unload time — not during transfer.
      const items = selected.map((x) => ({ batch_id: x.src.id, bags: x.bags }));
      const res = await api.post("/transfers", {
        from_location: from,
        to_location: to,
        items, vehicle, notes,
      });
      setSuccess(res.data);
      setPicks({}); setVehicle(""); setNotes("");
      await load();
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 sm:p-8">
      <PageHeader
        overline="WAREHOUSE LOGISTICS"
        title="Stock Transfer"
        description="Move approved batches between warehouses. The shipment keeps its source batch number while in transit — the receiving warehouse assigns new lot numbers at unload."
      />

      {success && (
        <div className="card p-4 mb-6 flex items-center gap-3" style={{ borderColor: "var(--green)" }} data-testid="transfer-success">
          <CheckCircle2 size={20} color="var(--green)" />
          <div>
            <div className="font-medium">Transfer logged — {success.from_location} → {success.to_location}</div>
            <div className="text-xs font-mono" style={{ color: "var(--text-muted)" }}>
              {success.total_bags} bags / {success.total_kg} kg ·{" "}
              {success.items.map((i) => i.source_batch_number).join(", ")} · awaiting unload at {success.to_location}
            </div>
          </div>
        </div>
      )}

      {/* Incoming shipments awaiting unload (in_transit batches at your warehouse) */}
      {incoming.length > 0 && (
        <div className="card overflow-hidden mb-6" data-testid="incoming-section">
          <div className="px-4 py-3 border-b flex items-center gap-2" style={{ borderColor: "var(--border)", background: "rgba(234,179,8,0.05)" }}>
            <PackageCheck size={16} color="var(--amber)" />
            <div className="font-heading text-sm font-semibold">
              INCOMING — Awaiting Unload {locked ? `@ ${home}` : "(all warehouses)"} · {incoming.length} consignment(s)
            </div>
          </div>
          <div style={{ overflowX: "auto" }}>
            <table className="table">
              <thead>
                <tr>
                  <th>Arriving Batch</th>
                  <th>At Location</th>
                  <th>From Source</th>
                  <th className="text-right">Bags</th>
                  <th>Status</th>
                  <th>Sent At</th>
                  <th>Shortage / Notes</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {incoming.map((b) => {
                  const draftKey = b.id;
                  const draft = shortageDrafts[draftKey] || {};
                  const currentNotes = draft.notes !== undefined ? draft.notes : (b.shortage_notes || "");
                  const currentBags = draft.bags !== undefined ? draft.bags : (b.shortage_bags || 0);
                  const dirty = (draft.notes !== undefined && draft.notes !== (b.shortage_notes || ""))
                    || (draft.bags !== undefined && parseInt(draft.bags, 10) !== (b.shortage_bags || 0));
                  const pending = (b.bags_remaining ?? b.bags_count) - (b.shortage_bags || 0);
                  return (
                  <tr key={b.id} data-testid={`incoming-row-${b.batch_number}`}>
                    <td className="font-mono font-medium">
                      {b.transfer_from?.batch_number || b.batch_number}
                    </td>
                    <td className="font-mono text-xs uppercase">{b.location}</td>
                    <td className="font-mono text-xs">
                      {b.transfer_from?.location || "—"}
                    </td>
                    <td className="font-mono text-right">
                      <div>{fmtNum(pending)}</div>
                      {(b.shortage_bags > 0 || (b.bags_remaining != null && b.bags_remaining !== b.bags_count)) && (
                        <span className="block text-[10px]" style={{ color: "var(--text-dim)" }}>
                          {fmtNum(b.bags_remaining ?? b.bags_count)} in transit
                          {b.shortage_bags > 0 ? ` − ${b.shortage_bags} short` : ""}
                        </span>
                      )}
                    </td>
                    <td><span className={statusBadgeClass(b.status)}>IN TRANSIT</span></td>
                    <td className="font-mono text-xs" style={{ color: "var(--text-muted)" }}>
                      {b.created_at?.slice(0, 16).replace("T", " ")}
                      {b.vehicle && <span className="block text-[10px]">veh: {b.vehicle}</span>}
                    </td>
                    <td style={{ minWidth: 280 }}>
                      <div className="flex gap-1 items-center flex-wrap">
                        <select
                          value={(draft.type !== undefined ? draft.type : (b.discrepancy_type || "short"))}
                          onChange={(e) => setShortageDrafts((p) => ({ ...p, [draftKey]: { ...(p[draftKey] || {}), type: e.target.value } }))}
                          style={{ padding: "4px 6px", fontSize: 12, width: 92 }}
                          data-testid={`discrepancy-type-${b.batch_number}`}
                          title="Direction of the discrepancy"
                        >
                          <option value="short">Short</option>
                          <option value="additional">Additional</option>
                        </select>
                        <input
                          type="number"
                          min="0"
                          value={currentBags || ""}
                          onChange={(e) => setShortageDrafts((p) => ({ ...p, [draftKey]: { ...(p[draftKey] || {}), bags: e.target.value } }))}
                          placeholder="0"
                          style={{ padding: "4px 6px", fontSize: 12, width: 56 }}
                          data-testid={`shortage-bags-${b.batch_number}`}
                          title="Bag count of the discrepancy"
                        />
                        <input
                          type="text"
                          value={currentNotes}
                          onChange={(e) => setShortageDrafts((p) => ({ ...p, [draftKey]: { ...(p[draftKey] || {}), notes: e.target.value } }))}
                          placeholder="reason / vehicle"
                          style={{ padding: "4px 8px", fontSize: 12, flex: 1, minWidth: 90 }}
                          data-testid={`shortage-input-${b.batch_number}`}
                        />
                        {dirty && (
                          <button
                            className="btn btn-secondary"
                            style={{ padding: "4px 8px", fontSize: 11 }}
                            onClick={() => saveShortage(b)}
                            disabled={savingShortage === b.id}
                            data-testid={`shortage-save-${b.batch_number}`}
                          >
                            Save
                          </button>
                        )}
                      </div>
                    </td>
                    <td>
                      <button
                        className="btn btn-primary"
                        style={{ padding: "4px 12px", fontSize: 12 }}
                        onClick={() => openSplitModal(b)}
                        data-testid={`unload-${b.batch_number}`}
                      >
                        <PackageCheck size={12} /> Unload &amp; Assign Lots
                      </button>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-2 text-xs font-mono" style={{ color: "var(--text-dim)", background: "var(--bg)" }}>
            → Stock will become available for dispatch/blending only after you press <span style={{ color: "var(--amber)" }}>Unload</span>.
          </div>
        </div>
      )}

      {/* Route */}
      <div className="card p-5 mb-6">
        <div className="text-xs font-mono mb-3" style={{ color: "var(--orange)" }}>// ROUTE</div>
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] gap-3 items-end">
          <div>
            <label>From Location{locked && " — locked"}</label>
            <select value={from} onChange={(e) => { setFrom(e.target.value); setPicks({}); }} disabled={locked} data-testid="from-location-select">
              {LOCATIONS.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
          </div>
          <div className="flex items-center justify-center pb-2"><ArrowRightLeft size={18} color="var(--orange)" /></div>
          <div>
            <label>To Location</label>
            <select value={to} onChange={(e) => setTo(e.target.value)} data-testid="to-location-select">
              {toLocationOptions.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 card overflow-hidden">
          <div className="px-4 py-3 border-b" style={{ borderColor: "var(--border)" }}>
            <div className="font-heading text-sm font-semibold">APPROVED BATCHES AT <span style={{ color: "var(--orange)" }}>{from}</span> ({sourceBatches.length})</div>
          </div>
          <div style={{ overflowX: "auto", maxHeight: "65vh", overflowY: "auto" }}>
            <table className="table">
              <thead>
                <tr>
                  <th>Source Batch</th>
                  <th>Prod. Date</th>
                  <th className="text-right">Avail.</th>
                  <th>Bags to Transfer</th>
                </tr>
              </thead>
              <tbody>
                {sourceBatches.length === 0 && (
                  <tr><td colSpan="4" className="text-center py-8" style={{ color: "var(--text-muted)" }}>No approved batches at {from}.</td></tr>
                )}
                {sourceBatches.map((b) => {
                  const pick = picks[b.id];
                  return (
                    <tr key={b.id} data-testid={`transfer-src-${b.batch_number}`}>
                      <td className="font-mono">{b.batch_number}</td>
                      <td className="font-mono text-xs">{b.production_date}</td>
                      <td className="font-mono text-right">{b.bags_remaining}</td>
                      <td style={{ width: 140 }}>
                        <input
                          type="number" min="0" max={b.bags_remaining}
                          value={pick?.bags || ""}
                          onChange={(e) => updateBags(b, e.target.value)}
                          placeholder="0"
                          style={{ padding: "4px 8px", fontFamily: "JetBrains Mono, monospace" }}
                          data-testid={`transfer-bags-${b.batch_number}`}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div className="card p-5">
          <div className="text-xs font-mono mb-2" style={{ color: "var(--orange)" }}>// CONFIRM</div>
          <div className="font-heading text-lg font-semibold mb-4 flex items-center gap-2"><Truck size={18} /> Transfer Summary</div>

          <div className="mb-4 p-3" style={{ background: "var(--bg)", border: "1px solid var(--border)" }}>
            <div className="text-xs font-mono" style={{ color: "var(--text-muted)" }}>{from} → {to}</div>
            <div className="font-heading text-3xl font-bold">{totalBags}</div>
            <div className="text-xs font-mono" style={{ color: "var(--text-dim)" }}>{totalBags * 50} KG · {selected.length} LOT(S)</div>
          </div>

          <div className="space-y-1 mb-4 max-h-48 overflow-y-auto">
            {selected.map((x) => (
              <div key={x.src.id} className="text-xs font-mono flex justify-between" style={{ color: "var(--text-muted)" }}>
                <span>{x.src.batch_number}</span>
                <span>{x.bags}b</span>
              </div>
            ))}
          </div>

          <div className="text-xs p-2.5 mb-3" style={{ background: "var(--brand-blue-soft)", border: "1px solid var(--border)", color: "var(--text-muted)", borderRadius: 6 }}>
            <span style={{ color: "var(--brand-blue)", fontWeight: 600 }}>i</span> The shipment keeps the <strong>source batch number</strong> while in transit. The receiver at <strong>{to}</strong> assigns new lot numbers during unload.
          </div>

          <label>Vehicle / LR No.</label>
          <input value={vehicle} onChange={(e) => setVehicle(e.target.value)} placeholder="Optional" data-testid="transfer-vehicle-input" />
          <label style={{ marginTop: 12 }}>Notes</label>
          <textarea rows="2" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="transfer-notes-input" />

          {err && (
            <div className="text-xs p-2 border mt-3" style={{ color: "var(--red)", borderColor: "var(--red)", background: "rgba(239,68,68,0.1)" }} data-testid="transfer-error">
              {err}
            </div>
          )}

          <button
            className="btn btn-primary w-full mt-4"
            disabled={loading || selected.length === 0}
            onClick={submit}
            data-testid="confirm-transfer-button"
          >
            <ArrowRightLeft size={16} /> {loading ? "Transferring..." : "Confirm Transfer"}
          </button>
        </div>
      </div>

      {/* History */}
      <div className="card overflow-hidden mt-8">
        <div className="px-4 py-3 border-b flex items-center justify-between gap-3 flex-wrap" style={{ borderColor: "var(--border)" }}>
          <div className="font-heading text-sm font-semibold">TRANSFER HISTORY</div>
          <div className="flex items-center gap-1" style={{ minWidth: 220 }}>
            <Search size={14} color="var(--text-muted)" />
            <input
              type="text"
              placeholder="Search batch, source, destination, vehicle…"
              value={historySearch}
              onChange={(e) => setHistorySearch(e.target.value)}
              style={{ padding: "6px 10px", fontSize: 13 }}
              data-testid="transfer-history-search"
            />
          </div>
        </div>
        {historyErr && (
          <div
            className="mx-4 mb-3 flex items-start gap-2 text-xs p-3 border"
            style={{ color: "var(--red)", borderColor: "var(--red)", background: "rgba(239,68,68,0.1)" }}
            data-testid="transfer-history-error"
          >
            <X size={14} className="mt-0.5 shrink-0" />
            <div className="flex-1">{historyErr}</div>
            <button onClick={() => setHistoryErr("")} className="btn btn-ghost" style={{ padding: 0, color: "var(--red)" }}>×</button>
          </div>
        )}
        <div style={{ overflowX: "auto" }}>
          <table className="table">
            <thead>
              <tr>
                <th>Date</th><th>From</th><th>To</th><th>Source Batches</th><th>Vehicle</th><th className="text-right">Bags</th><th className="text-right">KG</th><th>By</th>
                {canDeleteTransfer && <th></th>}
              </tr>
            </thead>
            <tbody>
              {filteredHistory.length === 0 && <tr><td colSpan={canDeleteTransfer ? 9 : 8} className="text-center py-8" style={{ color: "var(--text-muted)" }}>
                {history.length === 0 ? "No transfers yet." : "No transfers match your search."}
              </td></tr>}
              {filteredHistory.map((t) => (
                <tr key={t.id} data-testid={`transfer-row-${t.id}`}>
                  <td className="font-mono text-xs">{t.created_at.slice(0, 16).replace("T", " ")}</td>
                  <td className="font-mono text-xs">{t.from_location}</td>
                  <td className="font-mono text-xs" style={{ color: "var(--brand-blue)" }}>{t.to_location}</td>
                  <td className="font-mono text-xs">{t.items.map((i) => i.source_batch_number).join(", ")}</td>
                  <td className="font-mono text-xs">{t.vehicle || "—"}</td>
                  <td className="font-mono text-right">{fmtNum(t.total_bags)}</td>
                  <td className="font-mono text-right">{fmtNum(t.total_kg)}</td>
                  <td className="text-xs" style={{ color: "var(--text-muted)" }}>{t.created_by_name}</td>
                  {canDeleteTransfer && (
                    <td className="text-right">
                      <button
                        className="btn btn-ghost"
                        onClick={() => deleteTransfer(t)}
                        disabled={deletingTransferId === t.id}
                        title="Delete this transfer (soft-delete, recoverable)"
                        style={{ color: "var(--red)", padding: "4px 8px" }}
                        data-testid={`delete-transfer-${t.id}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Split-Unload Modal — destination receiver splits incoming shipment into N lots */}
      {splitTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-3" style={{ background: "rgba(0,0,0,0.6)" }} onClick={closeSplitModal}>
          <div className="card p-6 w-full max-w-3xl max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()} data-testid="split-unload-modal">
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="text-xs font-mono" style={{ color: "var(--amber)" }}>// UNLOAD &amp; ASSIGN LOT NUMBERS</div>
                <h3 className="font-heading text-xl font-bold mt-1">Assign lots for incoming shipment</h3>
              </div>
              <button className="btn btn-ghost" style={{ padding: 4 }} onClick={closeSplitModal} data-testid="split-close-btn"><X size={16} /></button>
            </div>

            <div className="p-3 mb-4 font-mono text-xs space-y-1" style={{ background: "var(--bg)", border: "1px solid var(--border)" }}>
              <div>Source batch: <span style={{ color: "var(--orange)" }}>{splitTarget.transfer_from?.batch_number}</span> @ {splitTarget.transfer_from?.location}</div>
              <div>
                Arriving at: <span style={{ color: "var(--text)" }}>{splitTarget.location}</span>
                {" "}· In transit: <span style={{ color: "var(--text)" }}>{splitTarget.bags_remaining ?? splitTarget.bags_count}</span> bags
                {(splitTarget.shortage_bags || 0) > 0 && (
                  <>
                    {" "}· <span style={{ color: "var(--red)" }}>shortage {splitTarget.shortage_bags}</span>
                    {" "}· <span style={{ color: "var(--brand-blue)", fontWeight: 600 }}>
                      unloadable {(splitTarget.bags_remaining ?? splitTarget.bags_count) - (splitTarget.shortage_bags || 0)}
                    </span>
                  </>
                )}
              </div>
              {splitTarget.shortage_notes && (
                <div style={{ color: "var(--text-muted)" }}>
                  Shortage notes: <em>{splitTarget.shortage_notes}</em>
                </div>
              )}
              <div>QC specs &amp; lab reports will be inherited automatically.</div>
            </div>

            <div className="text-sm mb-2" style={{ color: "var(--text-muted)" }}>
              Assign the bags you are unloading <strong>right now</strong> to one or more new lots. Any bags not assigned will stay in the incoming list for later unloading.
              {(splitTarget.shortage_bags || 0) > 0 && (
                <span style={{ color: "var(--red)" }}> The {splitTarget.shortage_bags}-bag shortage is already deducted from the unloadable count.</span>
              )}
            </div>

            <div className="space-y-2 mb-3">
              {splitLots.map((lot, idx) => (
                <div key={lot._id || idx} className="grid grid-cols-12 gap-2 items-end p-2" style={{ border: "1px solid var(--border)" }} data-testid={`split-lot-row-${idx}`}>
                  <div className="col-span-12 sm:col-span-4">
                    <label className="text-xs">Lot / Batch Number</label>
                    <input
                      type="text"
                      value={lot.batch_number}
                      onChange={(e) => updateLot(idx, "batch_number", e.target.value)}
                      placeholder="e.g. F-2026-001-A"
                      data-testid={`split-lot-number-${idx}`}
                    />
                  </div>
                  <div className="col-span-6 sm:col-span-2">
                    <label className="text-xs">Bags</label>
                    <input
                      type="number"
                      min="1"
                      value={lot.bags}
                      onChange={(e) => updateLot(idx, "bags", e.target.value)}
                      data-testid={`split-lot-bags-${idx}`}
                    />
                  </div>
                  <div className="col-span-6 sm:col-span-3">
                    <label className="text-xs">Unload Date</label>
                    <input
                      type="date"
                      value={lot.unload_date}
                      onChange={(e) => updateLot(idx, "unload_date", e.target.value)}
                      data-testid={`split-lot-date-${idx}`}
                    />
                  </div>
                  <div className="col-span-10 sm:col-span-2">
                    <label className="text-xs">Notes</label>
                    <input
                      type="text"
                      value={lot.notes}
                      onChange={(e) => updateLot(idx, "notes", e.target.value)}
                      placeholder="optional"
                      data-testid={`split-lot-notes-${idx}`}
                    />
                  </div>
                  <div className="col-span-2 sm:col-span-1 flex justify-end">
                    {splitLots.length > 1 && (
                      <button
                        className="btn btn-ghost"
                        style={{ padding: 6 }}
                        onClick={() => removeLotRow(idx)}
                        data-testid={`split-lot-remove-${idx}`}
                        title="Remove this lot"
                      >
                        <X size={14} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <button className="btn btn-secondary mb-4" onClick={addLotRow} data-testid="split-add-lot-btn">
              <Plus size={14} /> Add another lot
            </button>

            {(() => {
              const shortage = splitTarget.shortage_bags || 0;
              const pending = (splitTarget.bags_remaining ?? splitTarget.bags_count) - shortage;
              const total = splitLots.reduce((s, l) => s + (parseInt(l.bags, 10) || 0), 0);
              const diff = pending - total;
              const color = diff === 0 ? "var(--green)" : diff < 0 ? "var(--red)" : "var(--brand-blue)";
              return (
                <div className="p-3 mb-4 font-mono text-xs" style={{ background: "var(--bg)", border: `1px solid ${color}` }} data-testid="split-totals">
                  Unloading now: <span style={{ color }}>{total}</span> / {pending} bags unloadable
                  {diff > 0 && <span style={{ color: "var(--brand-blue)" }}> · {diff} bags will stay in transit for later unload</span>}
                  {diff < 0 && <span style={{ color: "var(--red)" }}> · {-diff} bags over the unloadable amount — reduce lot sizes</span>}
                  {diff === 0 && <span style={{ color: "var(--green)" }}> · fully unloads this shipment</span>}
                </div>
              );
            })()}

            {splitErr && (
              <div className="card p-3 mb-3" style={{ borderColor: "var(--red)", background: "rgba(239,68,68,0.06)", color: "var(--red)" }} data-testid="split-err">
                {splitErr}
              </div>
            )}

            <div className="flex gap-3">
              <button className="btn btn-secondary flex-1" onClick={closeSplitModal} data-testid="split-cancel-btn">Cancel</button>
              <button
                className="btn btn-primary flex-1"
                onClick={submitSplit}
                disabled={splitLoading}
                data-testid="split-confirm-btn"
              >
                <PackageCheck size={14} /> {splitLoading ? "Unloading…" : "Confirm Unload"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
